import { useState, useEffect } from "react";
import { useLang, ui, tx } from "../i18n";
import { Icon } from "./Icon";

export function Header({ lowBandwidth, onToggleLowBandwidth, highContrast, onToggleHighContrast }) {
  const { lang, setLang } = useLang();
  const [installEvt, setInstallEvt] = useState(null);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvt(() => e); };
    const onInstalled = () => { setInstallEvt(null); setShowHint(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (installEvt) {
      installEvt.prompt();
      try { await installEvt.userChoice; } catch { /* usuario cerró el diálogo de instalación */ }
      setInstallEvt(null);
    } else {
      setShowHint((v) => !v);
    }
  };
  return (
    <header className="no-print sticky top-0 z-40 bg-[#1C2B4A] text-white border-b border-slate-700">
      <div className="px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-3">
        <a href="#" data-testid="site-logo" className="flex items-center gap-2.5 min-w-0" onClick={(e) => e.preventDefault()}>
          <img src="/logo.png" alt="preparatepr.org" className="h-10 w-10 shrink-0 rounded-full ring-2 ring-white/80 bg-white" />
          <span className="font-display font-black tracking-tight text-lg sm:text-xl truncate">
            preparatepr<span className="text-amber-500">.org</span>
          </span>
        </a>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="relative">
            <button
              data-testid="install-app-btn"
              onClick={install}
              title={lang === "es" ? "Instalar app" : "Install app"}
              className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border border-slate-500 text-slate-200 hover:border-amber-500 hover:text-amber-400 transition-colors duration-200"
            >
              <Icon name="Download" className="w-4 h-4" />
              <span className="hidden md:inline">{lang === "es" ? "Instalar app" : "Install app"}</span>
            </button>
            {showHint && !installEvt && (
              <div
                data-testid="install-app-hint"
                className="fixed right-4 left-4 sm:left-auto top-20 z-50 sm:w-64 rounded-md border border-slate-600 bg-[#16223C] p-3 pr-8 text-xs leading-snug text-slate-200 shadow-xl"
              >
                {lang === "es"
                  ? "En el menú de tu navegador elige «Añadir a pantalla de inicio» o «Instalar app»."
                  : "In your browser menu choose “Add to Home Screen” or “Install app”."}
                <button
                  data-testid="install-app-hint-close"
                  onClick={() => setShowHint(false)}
                  aria-label={lang === "es" ? "Cerrar" : "Close"}
                  className="absolute right-2 top-2 text-slate-400 hover:text-white transition-colors duration-150"
                >
                  <Icon name="X" className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
          <button
            data-testid="high-contrast-toggle"
            onClick={onToggleHighContrast}
            title={lang === "es" ? "Alto contraste" : "High contrast"}
            aria-pressed={highContrast}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors duration-200 ${
              highContrast
                ? "bg-amber-600 border-amber-500 text-white"
                : "border-slate-500 text-slate-200 hover:border-amber-500 hover:text-amber-400"
            }`}
          >
            <Icon name="Eye" className="w-4 h-4" />
            <span className="hidden lg:inline">{lang === "es" ? "Alto contraste" : "High contrast"}</span>
          </button>
          <button
            data-testid="low-bandwidth-toggle"
            onClick={onToggleLowBandwidth}
            title={tx(ui.lowBandwidth, lang)}
            aria-pressed={lowBandwidth}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors duration-200 ${
              lowBandwidth
                ? "bg-amber-600 border-amber-500 text-white"
                : "border-slate-500 text-slate-200 hover:border-amber-500 hover:text-amber-400"
            }`}
          >
            <Icon name="Gauge" className="w-4 h-4" />
            <span className="hidden sm:inline">{tx(ui.lowBandwidth, lang)}</span>
          </button>
          <div data-testid="bilingual-toggle" className="flex rounded-full border border-slate-500 overflow-hidden">
            {["es", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
                  lang === l ? "bg-amber-600 text-white" : "text-slate-300 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
